import LockIcon from '@mui/icons-material/Lock'
import { Grid, MenuItem, TextField, Typography, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { createDependenciesList, dependenciesNameMapping } from './ncoTaskObjects'
import { OrganizationTableItem } from 'types'

interface ChannelsProps {
  deal: OrganizationTableItem
  handleChange: (field: string, value: string) => void
  checklistProgress?: Record<string, number>
}

const Channels: React.FC<ChannelsProps> = ({ deal, handleChange, checklistProgress }) => {
  const theme = useTheme()
  const ncoDependenciesList = useMemo(() => {
    if (!deal) return []
    return createDependenciesList(deal) ?? []
  }, [deal])

  // useEffect(() => {
  //   if (ncoDependenciesList) console.log('ncoDependenciesList', ncoDependenciesList)
  // }, [ncoDependenciesList])

  const initializeSelectedStatuses = () => {
    const initialStatuses = {}
    ncoDependenciesList[0]?.Dependency?.forEach(dep => {
      const key = Object.keys(dep)[0]
      initialStatuses[key] = deal?.[key] || 'N/A'
    })
    return initialStatuses
  }

  const [selectedStatuses, setSelectedStatuses] = useState(initializeSelectedStatuses())

  useEffect(() => {
    setSelectedStatuses(initializeSelectedStatuses())
  }, [deal, ncoDependenciesList])

  const handleCardClick = (dependency, statusOptions) => {
    const currentIndex = statusOptions.findIndex(option => option.name === selectedStatuses[dependency])
    const nextIndex = (currentIndex + 1) % statusOptions.length
    const updatedStatus = statusOptions[nextIndex].name

    setSelectedStatuses(prev => ({
      ...prev,
      [dependency]: updatedStatus,
    }))

    handleChange(dependency, updatedStatus)
  }

  // Utility to format a decimal as a percentage string.
  const formatPercentage = decimal => `${Math.round(decimal * 100)}%`

  // Helper to determine color based on progress fraction
  const getChannelColor = progress => {
    if (progress === 1) return 'rgb(51, 211, 145)' // fully complete (green)
    if (progress === 0) return 'rgb(121, 126, 147)' // not started (grey)
    return 'rgb(253, 188, 100)' // partially complete (amber)
  }

  return (
    <Grid container spacing={3} sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
      {ncoDependenciesList[0]?.Dependency?.map((dep, index) => {
        const dependencyKey = Object.keys(dep)[0]
        const normalizedKey = dependencyKey.toLowerCase()
        const dependencyName = dependenciesNameMapping[dependencyKey] || dependencyKey
        const statusOptions = dep[dependencyKey]

        // For certain channels, display the progress percentage if available.
        let currentStatus
        if (
          ['fbpage', 'gtm', 'ga4', 'completeseoform'].includes(normalizedKey) &&
          checklistProgress?.[normalizedKey] != null
        ) {
          currentStatus = formatPercentage(checklistProgress[normalizedKey])
        } else {
          currentStatus = selectedStatuses[dependencyKey]
        }

        // Use our helper for color if we have a progress-based channel,
        // otherwise, fall back to the color provided in statusOptions.
        const currentStatusColor =
          ['fbpage', 'gtm', 'ga4', 'completeseoform'].includes(normalizedKey) &&
          checklistProgress?.[normalizedKey] != null
            ? getChannelColor(checklistProgress[normalizedKey])
            : statusOptions.find(option => option.name === currentStatus)?.color || theme.palette.grey[300]

        return (
          <Grid
            item
            xs={3}
            sm={3}
            md={3}
            lg={1.7}
            xl={1.7}
            key={index}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            {['fbpage', 'gtm', 'ga4', 'completeseoform'].includes(normalizedKey) &&
            checklistProgress?.[normalizedKey] != null ? (
              <TextField
                label={<Typography sx={{ color: 'rgba(0,0,0,1)' }}>{dependencyName}</Typography>}
                value={currentStatus} // like "0%" or "80%"
                size="small"
                fullWidth
                disabled
                InputLabelProps={{
                  sx: {
                    fontSize: '2rem', // <<< Set it larger here
                    fontWeight: 'bold', // (optional) make it bold if you want it even clearer
                    color: theme => theme.palette.text.primary,
                  },
                }}
                InputProps={{
                  endAdornment: <LockIcon sx={{ color: theme.palette.text.secondary }} />,
                  sx: {
                    minWidth: 120,
                    borderRadius: 1,
                  },
                }}
                sx={{
                  WebkitTextFillColor: theme.palette.text.primary,
                  '& .MuiInputBase-root': {
                    color: currentStatusColor,
                  },
                  '& .MuiInputBase-input': {
                    WebkitTextFillColor: currentStatusColor,
                  },

                  '& .Mui-disabled': {
                    color: currentStatusColor,
                  },
                }}
              />
            ) : (
              <TextField
                select
                label={dependencyName}
                value={currentStatus} // "N/A", "Done", etc
                onChange={e => {
                  const updatedStatus = e.target.value
                  setSelectedStatuses(prev => ({
                    ...prev,
                    [dependencyKey]: updatedStatus,
                  }))
                  handleChange(dependencyKey, updatedStatus)
                }}
                size="small"
                fullWidth
                InputProps={{
                  sx: {
                    minWidth: 120,
                    // backgroundColor: currentStatusColor,
                    color: currentStatusColor,
                    borderRadius: 1,
                  },
                }}
                sx={{
                  WebkitTextFillColor: theme.palette.text.primary,
                  '& .MuiInputBase-root': {
                    color: currentStatusColor,
                  },
                  '& .MuiInputBase-input': {
                    WebkitTextFillColor: currentStatusColor,
                  },

                  '& .Mui-disabled': {
                    color: currentStatusColor,
                  },
                }}
              >
                {statusOptions.map(option => (
                  <MenuItem key={option.name} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
        )
      })}
    </Grid>
  )
}

export default React.memo(Channels)
